import { useCallback, useMemo, useState } from 'react';
import { BookOpen, Loader2 } from 'lucide-react';

import { Filter } from '@/components/filter';

import { useKnowledgeBasesQuery } from '@/query/knowledge.query';

import { useKnowledgeModalsStore } from '@/store/knowledge-modals.store';

export function KnowledgeBaseSidebar() {
  const selectedKnowledgeBase = useKnowledgeModalsStore(state => state.selectedKnowledgeBase);
  const selectKnowledgeBase = useKnowledgeModalsStore(state => state.selectKnowledgeBase);
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading } = useKnowledgeBasesQuery({
    page: 1,
    page_size: 100,
    query: '',
  });

  const knowledgeBases = data?.data || [];

  const handleFilter = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const filteredKnowledgeBases = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return knowledgeBases;
    return knowledgeBases.filter(kb => kb.name.toLowerCase().includes(normalizedSearch));
  }, [searchTerm, knowledgeBases]);

  return (
    <div className="w-60 flex-shrink-0 border rounded-lg overflow-hidden flex flex-col">
      <div className="border-b bg-muted/30">
        <div className="flex items-center justify-between gap-2 px-3 pb-2 pt-3">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold">Knowledge Bases</span>
            <span className="text-xs text-muted-foreground">({filteredKnowledgeBases.length})</span>
          </div>
        </div>
        <div className="px-3 pb-3">
          <Filter
            placeholder="Search..."
            initialValue={searchTerm}
            onFilter={handleFilter}
            onClear={handleClearSearch}
            debounceValue={300}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          filteredKnowledgeBases.map(kb => (
            <button
              key={kb.kb_id}
              onClick={() => selectKnowledgeBase(kb)}
              className={`flex w-full items-center gap-3 border-b px-3 py-3.5 text-left transition-colors ${
                selectedKnowledgeBase?.kb_id === kb.kb_id ? 'bg-muted/50' : 'hover:bg-muted/30'
              }`}
            >
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-primary-foreground" />
                </div>
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="font-medium text-sm truncate">{kb.name}</p>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
